import React from 'react';
import { Globe, Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
  updateData: (updates: Partial<OnboardingFormData>) => void;
}

const StepWebsiteUrls: React.FC<Props> = ({ data, updateData }) => {
  const addUrl = () => {
    updateData({ additionalUrls: [...data.additionalUrls, ''] });
  };

  const updateUrl = (index: number, value: string) => {
    const newUrls = [...data.additionalUrls];
    newUrls[index] = value;
    updateData({ additionalUrls: newUrls });
  };

  const removeUrl = (index: number) => {
    updateData({
      additionalUrls: data.additionalUrls.filter((_, i) => i !== index),
    });
  };

  return (
    <div className="space-y-6">
      <p className="text-muted-foreground">
        Provide your website URL(s) so we can index your content and train your assistant 
        on your services, team, and other public information.
      </p>

      {/* Primary URL */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Primary Website URL <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={data.primaryWebsiteUrl}
            onChange={(e) => updateData({ primaryWebsiteUrl: e.target.value })}
            placeholder="https://yourcompany.com"
            className="pl-10"
          />
        </div>
      </div>

      {/* Additional URLs */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Additional URLs (Optional)
        </label>
        <p className="text-xs text-muted-foreground mb-2">
          Add any additional pages or subdomains you'd like us to index.
        </p>

        {data.additionalUrls.map((url, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="relative flex-1">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={url}
                onChange={(e) => updateUrl(index, e.target.value)}
                placeholder="https://..."
                className="pl-10"
              />
            </div>
            <Button variant="ghost" size="icon" onClick={() => removeUrl(index)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}

        <Button variant="outline" size="sm" onClick={addUrl} className="mt-2">
          <Plus className="h-4 w-4 mr-2" />
          Add Another URL
        </Button>
      </div>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4">
        <p className="text-sm text-card-foreground">
          <strong>💡 Tip:</strong> Include your services page, team page, and any FAQ pages. 
          We'll automatically crawl linked pages from your primary URL as well.
        </p>
      </div>
    </div>
  );
};

export default StepWebsiteUrls;
